import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

/** Skeleton de um MetricCard (espelha o layout real: título + chip + número + subtítulo). */
export function MetricCardSkeleton() {
  return (
    <div className="flex flex-col gap-2 rounded-xl border border-border bg-card p-4 shadow-card sm:p-5">
      <div className="flex items-start justify-between gap-2">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="size-9 rounded-lg" />
      </div>
      <Skeleton className="mt-1 h-9 w-20" />
      <Skeleton className="mt-1 h-3 w-28" />
    </div>
  );
}

/** Linha de N MetricCards. */
export function MetricCardsRowSkeleton({ count = 4 }: { count?: number }) {
  return (
    <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:gap-6 xl:grid-cols-4">
      {Array.from({ length: count }).map((_, i) => (
        <MetricCardSkeleton key={i} />
      ))}
    </div>
  );
}

/** Skeleton de um DeviceCard (header + badges + meta + stats + ações). */
export function DeviceCardSkeleton() {
  return (
    <div className="flex flex-col gap-4 rounded-xl border border-border bg-card p-5 shadow-card">
      <div className="flex items-center gap-3">
        <Skeleton className="size-10 shrink-0 rounded-lg" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-4 w-28" />
          <Skeleton className="h-3 w-20" />
        </div>
      </div>
      <div className="flex gap-2">
        <Skeleton className="h-5 w-16 rounded-full" />
        <Skeleton className="h-5 w-14 rounded-full" />
      </div>
      <div className="space-y-2">
        <Skeleton className="h-3 w-40" />
        <Skeleton className="h-3 w-28" />
        <Skeleton className="h-3 w-36" />
      </div>
      <Skeleton className="h-16 w-full rounded-lg" />
      <div className="flex gap-2 border-t border-border pt-4">
        <Skeleton className="h-8 flex-1 rounded-lg" />
        <Skeleton className="h-8 flex-1 rounded-lg" />
      </div>
    </div>
  );
}

/** Grid de N DeviceCards skeleton. */
export function DeviceGridSkeleton({ count = 8 }: { count?: number }) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:gap-5 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {Array.from({ length: count }).map((_, i) => (
        <DeviceCardSkeleton key={i} />
      ))}
    </div>
  );
}

/**
 * Skeleton genérico de tabela. `columns` = array de classes de largura (ex: ['w-40','w-16'])
 * que definem o nº de colunas e a largura aproximada de cada barra.
 */
export function TableSkeleton({
  rows = 6,
  columns = ['w-40', 'w-20', 'w-24', 'w-16', 'w-28'],
}: {
  rows?: number;
  columns?: string[];
}) {
  return (
    <div className="divide-y divide-border">
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="flex items-center gap-6 px-6 py-4">
          {columns.map((w, c) => (
            <Skeleton key={c} className={cn('h-4', w, c === columns.length - 1 && 'ml-auto')} />
          ))}
        </div>
      ))}
    </div>
  );
}

/** Skeleton de um card de gráfico (título + área do plot). */
export function ChartCardSkeleton({
  className,
  height = 300,
}: {
  className?: string;
  height?: number;
}) {
  return (
    <div
      className={cn(
        'flex flex-col gap-4 rounded-xl border border-border bg-card p-6 shadow-card',
        className
      )}
    >
      <Skeleton className="h-5 w-48" />
      <Skeleton className="w-full rounded-lg" style={{ height }} />
    </div>
  );
}

/** Barra de distribuição segmentada (Errors/Devices) em estado de carregamento. */
export function DistributionBarSkeleton() {
  return (
    <div className="rounded-xl border border-border bg-card p-4 shadow-card lg:p-5">
      <div className="mb-3 flex items-center justify-between">
        <Skeleton className="h-4 w-44" />
        <Skeleton className="h-3 w-16" />
      </div>
      <Skeleton className="h-2.5 w-full rounded-full" />
      <div className="mt-3 flex flex-wrap gap-5">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-3 w-20" />
        ))}
      </div>
    </div>
  );
}
